import { z } from "zod";
import {
  CONTENT_FITNESS_VERIFICATION_STATE,
  ContentFitnessCoverageStatusSchema,
  ContentFitnessVerificationStateSchema,
} from "../../../enums/content-fitness";
import { OriginFileKindSchema } from "../../../enums/scrape";
import {
  CheckJudgmentSchema,
  EvidenceKindSchema,
  FindingSeveritySchema,
} from "../../../enums/yeet";
import { ContentFitnessSubcriterionKeySchema } from "../../../report/content-fitness/subcriteria";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "../../../report/content-fitness/version";
import { IssueSemanticFactsSchema } from "../../issue/semantics";
import { ProblemKeySchema } from "../problem-key";

/** Assessment contracts: submit_finding and submit_checks. */

export const FindingEvidenceSchema = z.object({
  kind: EvidenceKindSchema,
  value: z.string().min(1),
});
export type FindingEvidence = z.infer<typeof FindingEvidenceSchema>;

export const SubmitFindingInputSchema = z.object({
  // Exact key from list_metrics; unknown keys are rejected, never coerced.
  metricKey: z.string().min(1),
  primarySubcriterionKey: ContentFitnessSubcriterionKeySchema.optional(),
  // Names the underlying problem, not the page: the same defect on another
  // page carries the same key, and distinct problems under one subcriterion
  // carry distinct keys.
  problemKey: ProblemKeySchema,
  originResourceKind: OriginFileKindSchema.optional().describe(
    "Set only when the defect IS the origin file itself (missing, malformed, or blanket-blocking directives), after reading it with read_origin_file. Never set it for this page's downstream effects of that file — those stay page-scoped.",
  ),
  semanticFacts: IssueSemanticFactsSchema.describe(
    "Structured facts already known while reporting this defect. Use heading for exact outline defects, wording for corrections/context, access_gate for blocking controls, journey for navigation/onward relevance, origin_condition for the origin resource state, and architecture for supported estate-change evidence. Use an empty array when none applies; never recover these facts from the title later.",
  ),
  severity: FindingSeveritySchema,
  title: z
    .string()
    .min(1)
    .describe("A factual problem statement, without a Finding: prefix."),
  detail: z
    .string()
    .min(1)
    .describe(
      "A concise factual explanation (maximum three sentences) grounded in owned-site evidence. Do not claim external citations, representation, traffic, conversion, revenue, competitor outcomes or measured user behaviour.",
    ),
  evidence: z.array(FindingEvidenceSchema).min(1),
  recommendedAction: z
    .string()
    .describe(
      "One realistic primary remediation action (maximum two sentences), without a RecommendedAction: prefix or unsupported outcome claim.",
    )
    .optional(),
});
export type SubmitFindingInput = z.infer<typeof SubmitFindingInputSchema>;

export const SubmitFindingOutputSchema = z.object({
  findingId: z.uuid(),
});
export type SubmitFindingOutput = z.infer<typeof SubmitFindingOutputSchema>;

export const CheckJudgmentEntrySchema = z.object({
  // A checkKey from this dimension's list_metrics checks; unknown keys 422.
  checkKey: z.string().min(1),
  judgment: CheckJudgmentSchema,
  // Required for yes/no (the evidence that makes the judgment defensible).
  evidence: z.array(FindingEvidenceSchema).optional(),
  // Required for not_applicable (why) and unable_to_verify (what was tried).
  rationale: z.string().min(1).optional(),
});
export type CheckJudgmentEntry = z.infer<typeof CheckJudgmentEntrySchema>;

export const SubmitChecksInputSchema = z.object({
  // One call per dimension, covering every one of its checks exactly once.
  metricKey: z.string().min(1),
  judgments: z.array(CheckJudgmentEntrySchema).min(1),
});
export type SubmitChecksInput = z.infer<typeof SubmitChecksInputSchema>;

export const SubmitChecksOutputSchema = z.object({
  accepted: z.literal(true),
  // Points earned by yes judgments over the points of all applicable (yes/no)
  // checks. The agent sees the outcome but never sets it.
  earnedPoints: z.number().int().min(0),
  applicablePoints: z.number().int().min(0),
});
export type SubmitChecksOutput = z.infer<typeof SubmitChecksOutputSchema>;

export const SubcriterionAssessmentEntrySchema = z
  .object({
    subcriterionKey: ContentFitnessSubcriterionKeySchema,
    verificationState: ContentFitnessVerificationStateSchema,
    score: z.number().int().min(0).max(4).optional(),
    rationale: z.string().min(1),
    evidence: z.array(FindingEvidenceSchema).optional(),
  })
  .superRefine((entry, context) => {
    if (entry.verificationState === CONTENT_FITNESS_VERIFICATION_STATE.SCORED) {
      if (entry.score === undefined) {
        context.addIssue({
          code: "custom",
          path: ["score"],
          message: "A scored subcriterion assessment requires score 0-4.",
        });
      }
      if ((entry.evidence?.length ?? 0) === 0) {
        context.addIssue({
          code: "custom",
          path: ["evidence"],
          message:
            "A scored subcriterion assessment requires at least one evidence item.",
        });
      }
      return;
    }
    if (entry.score !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["score"],
        message:
          "not_applicable and unable_to_verify assessments must not include a score.",
      });
    }
  });
export type SubcriterionAssessmentEntry = z.infer<
  typeof SubcriterionAssessmentEntrySchema
>;

export const SubmitSubcriterionAssessmentsInputSchema = z.object({
  frameworkVersion: z.literal(CONTENT_FITNESS_FRAMEWORK_VERSION),
  parentMetricKey: z.string().min(1),
  assessments: z.array(SubcriterionAssessmentEntrySchema).min(1),
});
export type SubmitSubcriterionAssessmentsInput = z.infer<
  typeof SubmitSubcriterionAssessmentsInputSchema
>;

export const SubmitSubcriterionAssessmentsOutputSchema = z.object({
  accepted: z.literal(true),
  parentMetricKey: z.string(),
  dimensionScore: z.number().int().min(0).max(100).nullable(),
  assessedSubcriteria: z.number().int().min(0),
  canonicalSubcriteria: z.number().int().min(0),
  coverageStatus: ContentFitnessCoverageStatusSchema,
});
export type SubmitSubcriterionAssessmentsOutput = z.infer<
  typeof SubmitSubcriterionAssessmentsOutputSchema
>;
